import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ArticleRow from "@/components/feature/ArticleRow";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { filterArticles, formatViews, getHotMovies, paginate, totalPages } from "@/lib/media";
import { newsCategories } from "@/mocks/site";

const PAGE_SIZE = 5;

export default function NewsPage() {
  const [category, setCategory] = useState<string>("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => filterArticles(category), [category]);
  const featured = filtered[0];
  const rest = filtered.slice(1);
  const maxPage = totalPages(rest.length, PAGE_SIZE);
  const currentPage = Math.min(page, maxPage);
  const list = paginate(rest, currentPage, PAGE_SIZE);

  useDocumentMeta({
    title: `影视资讯 - 色虎影视资讯列表`,
    description:
      "色虎影视资讯频道提供最新影视行业报道、深度影评与幕后专题，涵盖电影、电视剧、综艺与动画的行业动态与观剧指南。",
    keywords: "色虎资讯,影视新闻,影评,影视专题,行业动态,新片预告",
  });

  const hotMovies = getHotMovies(5);

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="portal-container py-8 md:py-10">
          <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-foreground-600">影视资讯</span>
          </nav>
          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-accent-600">
                <i className="ri-newspaper-line text-base" aria-hidden="true" />
                Film &amp; TV Newsroom
              </p>
              <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">影视资讯</h1>
              <p className="mt-2 max-w-2xl text-sm leading-7 text-foreground-600">
                行业动态、深度影评、幕后专题与新片预告，色虎编辑部每日整理。所有内容均为演示数据，用于展示资讯阅读体验。
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <p className="font-heading text-2xl font-bold text-primary-700">{filtered.length}</p>
                <p className="text-[11px] text-foreground-400">篇资讯</p>
              </div>
              <div className="h-8 w-px bg-background-300" aria-hidden="true" />
              <div>
                <p className="font-heading text-2xl font-bold text-primary-700">4</p>
                <p className="text-[11px] text-foreground-400">个分类</p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {newsCategories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setCategory(item);
                  setPage(1);
                }}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  category === item
                    ? "bg-primary-500 font-medium text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {item}
                {item !== "全部" && (
                  <span className="ml-1 text-[10px] opacity-70">{filterArticles(item).length}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="portal-container mt-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            {filtered.length === 0 ? (
              <EmptyState
                icon="ri-file-search-line"
                title="该分类暂无资讯"
                description="换一个分类标签看看，或返回全部资讯浏览最新内容。"
                actionLabel="查看全部资讯"
                onAction={() => {
                  setCategory("全部");
                  setPage(1);
                }}
              />
            ) : (
              <>
                {featured && (
                  <Link
                    to={`/news/detail/${featured.id}`}
                    className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 md:grid-cols-2"
                  >
                    <div className="aspect-[16/10] w-full overflow-hidden bg-background-200 md:aspect-auto md:h-full md:min-h-[260px]">
                      <img
                        src={featured.thumbnail}
                        alt={featured.title}
                        title={`${featured.title} 影视资讯 色虎`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-col justify-center p-5 md:p-6">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded bg-accent-500 px-2 py-0.5 font-medium text-primary-950">头条</span>
                        <span className="text-foreground-400">{featured.category}</span>
                        <span className="text-foreground-400">{featured.date}</span>
                      </div>
                      <h2 className="mt-3 font-heading text-xl font-bold leading-8 text-foreground-950 transition-colors group-hover:text-accent-600 md:text-2xl">
                        {featured.title}
                      </h2>
                      <p className="mt-2 line-clamp-3 text-sm leading-7 text-foreground-500">{featured.summary}</p>
                      <p className="mt-3 flex items-center gap-2 text-xs text-foreground-400">
                        <span>{featured.author}</span>
                        <span className="h-1 w-1 rounded-full bg-background-400" />
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line" aria-hidden="true" />
                          {formatViews(featured.views)}
                        </span>
                      </p>
                    </div>
                  </Link>
                )}

                <div className="mt-4 rounded-lg border border-background-200 bg-background-50 px-4 py-1 md:px-5">
                  {list.map((article) => (
                    <ArticleRow key={article.id} article={article} />
                  ))}
                </div>

                <Pagination page={currentPage} total={maxPage} onChange={setPage} />
              </>
            )}
          </div>

          <aside className="space-y-6">
            <section className="card-surface p-4">
              <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-sm font-semibold text-foreground-950">
                <i className="ri-fire-line text-accent-500" aria-hidden="true" />
                热门影片
              </h2>
              <ol className="mt-1">
                {hotMovies.map((movie, index) => (
                  <li key={movie.id} className="border-b border-background-100 last:border-b-0">
                    <Link to={`/detail/${movie.id}`} className="group flex items-center gap-3 py-3">
                      <span className={`font-heading text-sm font-bold ${index < 3 ? "text-accent-600" : "text-foreground-300"}`}>
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground-800 group-hover:text-accent-600">{movie.title}</p>
                        <p className="mt-0.5 text-[11px] text-foreground-400">{movie.genres.slice(0, 2).join(" / ")}</p>
                      </div>
                      <span className="text-xs font-medium text-primary-700">{movie.rating.toFixed(1)}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </section>

            <section className="card-surface p-4">
              <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-sm font-semibold text-foreground-950">
                <i className="ri-price-tag-3-line text-primary-600" aria-hidden="true" />
                资讯标签
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {["悬疑", "年度盘点", "幕后", "新番", "票房", "剧评", "行业观察", "治愈"].map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-md border border-background-200 bg-background-100 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-700"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </section>

            <section className="rounded-lg bg-primary-500 p-5 text-background-50">
              <h2 className="font-heading text-base font-bold">想找某部影片的报道？</h2>
              <p className="mt-2 text-xs leading-6 text-primary-100">
                全站搜索同时覆盖影视作品与资讯内容，输入片名即可查看相关资料与影评。
              </p>
              <Link
                to="/search?q=雾都谜案"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                搜索「雾都谜案」
                <i className="ri-search-line" aria-hidden="true" />
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}